import {Component, MeshComponent, Object3D, TextComponent} from '@wonderlandengine/api';
import {hapticFeedback} from '../utils/haptic-feedback.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {Cursor, CursorTarget} from '@wonderlandengine/components';
import {PhongMaterial} from '../types/materials.js';
import {CursorReticle, CursorType} from './cursor-reticle.js';
import {LevelManagerScene2} from '../scene2/level-manager-scene2.js';
import {LevelManagerSceneOpening} from '../scene-opening/level-manager-scene-opening.js';
import {quat, vec3} from 'gl-matrix';
import {SoundManagerInstance, Sounds} from '../managers/soundmanager.js';

const Margin = 0.025;
const LineHeight = 0.047;
const charWidth = 0.028;

/**
 * text-bubbles
 */
export class TextBubbles extends Component {
    static TypeName = 'text-bubbles';

    @property.bool()
    textStatic = false; /* If static the text will be displayed without animation */
    @property.object({required: true})
    textObject!: Object3D;
    @property.object({required: true})
    textBubble!: Object3D;
    @property.object()
    objectHover?: Object3D | null; //Object Hover to Highlight
    @property.bool()
    disableClick = false;
    @property.int(50)
    delayLetters = 50; //Delay between each letter
    @property.int(1500)
    delayDisappear = 1500; //Delay to Text Disappear
    @property.float(0.021)
    bubbleWidthSpeed = 0.021;
    @property.object()
    levelManager?: Object3D; /* Level Manager to Send message to (optional) */
    @property.object({required: true})
    player!: Object3D;
    @property.string()
    msgToLvlManager_OnClick =
        ''; /* Send a message to level-manager on StartClick (optional) */
    @property.string()
    msgToLvlManager_OnEnd =
        ''; /* Send a message to level-manager on End/Reset Text (optional) */
    @property.bool(true)
    playTypeWriter = true;

    private textDisplay = '';
    private _delayLettersDefault = 0;
    private bubbleWidthMax = 0;
    private bubbleHeightMax = 0;
    private newlineCount = 0;
    private bubbleHeightPerLine = 0;
    private _textAppeared = false;
    private indexLetter = 0;
    private bubbleWidth = 0;
    private bubbleHeight = 0;
    private _displaying = false;
    private forceResetGate = false;
    private backScale = 0;
    private textComponent?: TextComponent;
    private levelManagerComponent?: LevelManagerScene2 | LevelManagerSceneOpening | null;

    // Properties
    get textAppeared() {
        return this._textAppeared;
    }

    get displaying() {
        return this._displaying;
    }
    set displaying(value) {
        this._displaying = value;
    }

    get delayLettersDefault() {
        return this._delayLettersDefault;
    }

    start() {
        /* Validate references */
        if (this.levelManager) {
            this.levelManagerComponent =
                this.levelManager.getComponent(LevelManagerScene2) ??
                this.levelManager.getComponent(LevelManagerSceneOpening);
        }

        const tempTextComponent = this.textObject.getComponent(TextComponent);
        if (!tempTextComponent) {
            throw new Error(
                `text-bubbles(${this.object.name}): textObject does not have a TextComponent`
            );
        }
        this.textComponent = tempTextComponent;

        /* Add Cursor Functions */
        const target =
            this.object.getComponent(CursorTarget) ||
            this.object.addComponent(CursorTarget)!;
        target.onHover.add(this.onHover.bind(this));
        target.onUnhover.add(this.onUnHover.bind(this));
        target.onDown.add(this.onDown.bind(this));
        //target.addUpFunction(this.onUp.bind(this));

        /* Clone objectHover material, to only affect this object's material */
        if (this.objectHover) {
            this.objectHover.getComponents(MeshComponent).forEach((meshComponent) => {
                const material = meshComponent.material;
                if (material) {
                    meshComponent.material = material.clone();
                }
            });
        }

        /* General */
        this.textDisplay = this.textComponent.text;
        this._delayLettersDefault = this.delayLetters;
        this.newlineCount = this.textDisplay.split('\n').length;

        const lines = this.textDisplay.split('\n');
        let maxChars = -1;
        for (let index = 0; index < lines.length; index++) {
            const element = lines[index];
            if (element.length > maxChars) {
                maxChars = element.length;
            }
        }

        /* Bubble Width & Height */
        if (this.textBubble) {
            this.textBubble.resetPositionRotation();

            const textRotation = quat.create();
            this.textObject.getRotationWorld(textRotation);
            this.textBubble.setRotationWorld(textRotation);

            const textPosition = vec3.create();
            this.textObject.getPositionWorld(textPosition);
            this.textBubble.setPositionWorld(textPosition);
            this.textBubble.translateObject([-Margin, Margin, -0.002]);

            this.bubbleHeightMax = this.newlineCount * LineHeight;
            this.bubbleWidthMax = maxChars * charWidth;

            this.bubbleWidthSpeed = charWidth;
            const scale = vec3.create();
            this.textObject.getScalingWorld(scale);
            this.backScale = scale[0] * 2;
        }

        this.bubbleHeightPerLine = this.bubbleHeightMax / this.newlineCount;

        /* Reset */
        this.resetSelf();
    }

    resetSelf() {
        if (this.textStatic) {
            return this;
        }
        this._textAppeared = false;
        this.indexLetter = 0;
        this.bubbleWidth = 0;
        this.bubbleHeight = LineHeight;
        if (this.textBubble) {
            this.textBubble.setScalingWorld([0, 0, 0]);
        }
        if (this.textObject) {
            this.textComponent!.text = '';
        }
        return this;
    }

    onHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.5, 50);
        /* Highlight */
        if (this.objectHover) {
            this.objectHover.getComponents(MeshComponent).forEach((meshComponent) => {
                //a.material.ambientColor = [0.5, 0.5, 0.5, 1];
                const material = meshComponent.material as PhongMaterial;
                if (material) {
                    material.emissiveColor = [0.12, 0.12, 0.12, 1];
                }
            });
        }

        /* Cursor */
        const cursorReticle = this.player.getComponent(CursorReticle);
        if (cursorReticle) {
            /* Cursor */
            cursorReticle.changeCursorTexture(cursor, CursorType.Pointer);
        }
    }

    onUnHover(_: Object3D, cursor: Cursor) {
        hapticFeedback(cursor.object, 0.3, 50);
        /* Highlight Reset to Default */
        if (this.objectHover) {
            this.objectHover.getComponents(MeshComponent).forEach((meshComponent) => {
                //a.material.ambientColor = [0.5, 0.5, 0.5, 1];
                const material = meshComponent.material as PhongMaterial;
                if (material) {
                    material.emissiveColor = [0, 0, 0, 1];
                }
            });
        }

        /* Cursor */
        const cursorReticle = this.player.getComponent(CursorReticle);
        if (cursorReticle) {
            /* Cursor */
            cursorReticle.changeCursorTexture(cursor, CursorType.Default);
        }
    }

    onDown(_: Object3D | undefined, cursor: Cursor | undefined) {
        if (this.disableClick) {
            return;
        }
        if (cursor) {
            hapticFeedback(cursor.object, 1.0, 20);
        }

        /* If Still writing, then finish Writing */
        if (this.displaying == true) {
            this.delayLetters = 0;
        } else {
            this.delayLetters = this.delayLettersDefault;
        }

        /* Already Displaying? Gate */
        if (this.displaying) {
            return;
        }

        /* Start Display Bubble Text */
        this.displayNextLetter();

        /* Send Message to LvlManager */
        if (this.levelManagerComponent && this.msgToLvlManager_OnClick.length !== 0) {
            this.levelManagerComponent
                .sendMessage(this.msgToLvlManager_OnClick)
                ?.catch(console.error);
        }
    }

    displayNextLetter() {
        /* Static? */
        if (this.textStatic) {
            return;
        }
        if (this.forceResetGate) {
            return;
        }

        /* Gate */
        this.displaying = true;
        /* Bubble Size */
        if (this.textBubble) {
            if (this.bubbleHeight == 0) {
                /* Set bubbleHeight by 1 line if it's 0 */
                this.bubbleHeight = LineHeight + Margin;
            }
            if (this.textDisplay.charAt(this.indexLetter) === '\n') {
                /* Increase bubbleHeight by 1 line if found a new line. */
                if (this.bubbleHeight == LineHeight) {
                    /* Reset to bubbleHeightPerLine method */
                    this.bubbleHeight = this.bubbleHeightPerLine;
                }
                this.bubbleHeight += this.bubbleHeightPerLine;
                this.textBubble.setScalingWorld([
                    (this.bubbleWidth + Margin * 2) * this.backScale,
                    (this.bubbleHeight + Margin * 2) * this.backScale,
                    1,
                ]);
            } else if (this.bubbleWidth < this.bubbleWidthMax) {
                /* Increase bubbleWidth based on letters length. */
                this.bubbleWidth = this.indexLetter * this.bubbleWidthSpeed;
                this.textBubble.setScalingWorld([
                    (this.bubbleWidth + Margin * 2) * this.backScale,
                    (this.bubbleHeight + Margin * 2) * this.backScale,
                    1,
                ]);
            }
        }

        /* Text Appear Letter by Letter */
        if (this.indexLetter < this.textDisplay.length) {
            this._textAppeared = false;
            this.textComponent!.text += this.textDisplay.charAt(
                this.indexLetter
            ); /* Add the next letter to the container */
            this.indexLetter++; /* Move to the next letter */
            if (
                this.indexLetter % 2 === 0 &&
                this.delayLetters > 0 &&
                this.playTypeWriter
            ) {
                SoundManagerInstance.playSound(Sounds.typeWriter);
            }
            setTimeout(
                () => this.displayNextLetter(),
                this.delayLetters
            ); /* Call this function again after the delay */
        } else {
            setTimeout(() => {
                this._textAppeared = true;
                this.displaying = false;
                this.resetSelf();

                /* Send Message to LvlManager */
                if (this.levelManagerComponent && this.msgToLvlManager_OnEnd.length !== 0) {
                    this.levelManagerComponent
                        .sendMessage(this.msgToLvlManager_OnEnd)
                        ?.catch(console.error);
                }
            }, this.delayDisappear);
        }
    }
}
